import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Instagram } from "lucide-react";
import { SmartImg } from "@/components/SmartImg";
import { useAuth } from "@/hooks/use-auth";
import { downloadAsJpeg } from "@/lib/download-jpeg";
import { transformedImageUrl } from "@/lib/image-url";

export type LightboxInfo = {
  image: string;
  caption?: string | null;
  photographerName?: string | null;
  photographerId?: string | null;
  region?: string | null;
  theme?: string | null;
  instagramHandle?: string | null;
  websiteUrl?: string | null;
  creatorType?: "photographer" | "visagist";
};

export function PhotoLightbox({
  info,
  onClose,
  onPrev,
  onNext,
  neighborImages,
}: {
  info: LightboxInfo | null;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  /** Adjacent image URLs to warm into the browser cache for instant prev/next. */
  neighborImages?: (string | null | undefined)[];
}) {
  const { user } = useAuth();
  const isVisagist = info?.creatorType === "visagist";
  const creatorLabel = isVisagist ? "Visagist" : "Fotograf";
  const dialogRef = useRef<HTMLDivElement>(null);

  // Preload neighbouring images (same transform as the displayed image) so
  // switching prev/next renders instantly from cache instead of refetching.
  useEffect(() => {
    if (!info || !neighborImages?.length) return;
    const imgs = neighborImages
      .filter((src): src is string => !!src)
      .map((src) => {
        const img = new Image();
        img.decoding = "async";
        img.src = transformedImageUrl(src, { width: 1600, resize: "contain" });
        return img;
      });
    return () => {
      imgs.forEach((img) => { img.src = ""; });
    };
  }, [info, neighborImages]);

  useEffect(() => {
    if (!info) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "ArrowLeft" && onPrev) {
        e.preventDefault();
        onPrev();
        return;
      }
      if (e.key === "ArrowRight" && onNext) {
        e.preventDefault();
        onNext();
        return;
      }
      // Focus trap: keep Tab focus inside the dialog
      if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && (active === first || !root.contains(active))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const prevFocused = document.activeElement as HTMLElement | null;
    // Move focus into the dialog when it opens
    dialogRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the element that opened the lightbox
      prevFocused?.focus?.();
    };
  }, [info, onClose, onPrev, onNext]);

  if (!info) return null;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col md:flex-row cursor-zoom-out focus:outline-none"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Bildansicht"
      tabIndex={-1}
    >
      {onPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-3 top-16 translate-y-0 md:top-1/2 md:-translate-y-1/2 z-10 border border-foreground bg-background w-11 h-11 flex items-center justify-center hover:bg-foreground hover:text-background transition-colors"
          aria-label="Vorheriges Bild"
        >
          ‹
        </button>
      )}
      {onNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-3 md:right-[356px] top-16 translate-y-0 md:top-1/2 md:-translate-y-1/2 z-10 border border-foreground bg-background w-11 h-11 flex items-center justify-center hover:bg-foreground hover:text-background transition-colors"
          aria-label="Nächstes Bild"
        >
          ›
        </button>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 z-10 eyebrow border border-foreground bg-background px-4 py-2 hover:bg-foreground hover:text-background transition-colors"
        aria-label="Schließen"
      >
        ✕ Schließen
      </button>
      <div className="flex-1 flex items-center justify-center p-4 md:p-10 min-h-0">
        <SmartImg
          source={info.image}
          transform={{ width: 1600, resize: "contain" }}
          srcSetWidths={[1200, 1600, 2000]}
          sizes="(min-width: 768px) calc(100vw - 340px), 100vw"
          decoding="async"
          fetchPriority="high"
          alt={info.caption ?? info.photographerName ?? "Foto"}
          className="max-h-full max-w-full object-contain no-bw"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <aside
        className="w-full md:w-[340px] md:h-full border-t md:border-t-0 md:border-l border-border bg-background p-6 md:p-8 overflow-y-auto cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow text-muted-foreground">Bildinfo</div>
        {info.caption && (
          <p className="serif italic text-xl mt-4 leading-snug">{info.caption}</p>
        )}
        <dl className="mt-8 space-y-5 text-sm">
          {info.photographerName && (
            <div>
              <dt className="eyebrow text-muted-foreground mb-1">{creatorLabel}</dt>
              <dd className="serif text-lg">
                {info.photographerId ? (
                  isVisagist ? (
                    <Link
                      to="/visagists/$id"
                      params={{ id: info.photographerId }}
                      className="border-b border-foreground hover:opacity-70"
                    >{info.photographerName}</Link>
                  ) : (
                  <Link
                    to="/photographers/$id"
                    params={{ id: info.photographerId }}
                    className="border-b border-foreground hover:opacity-70"
                  >{info.photographerName}</Link>
                  )
                ) : info.photographerName}
              </dd>
            </div>
          )}
          {info.region && (
            <div>
              <dt className="eyebrow text-muted-foreground mb-1">Ort</dt>
              <dd className="serif">{info.region}</dd>
            </div>
          )}
          {info.theme && (
            <div>
              <dt className="eyebrow text-muted-foreground mb-1">Thema</dt>
              <dd className="serif capitalize">{info.theme}</dd>
            </div>
          )}
          {(info.instagramHandle || info.websiteUrl) && (
            <div className="pt-4 border-t border-border flex flex-wrap gap-2 eyebrow">
              {info.instagramHandle && (
                <a
                  href={`https://instagram.com/${info.instagramHandle.replace(/^@/, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 border border-foreground px-3 py-2 hover:bg-foreground hover:text-background transition-colors"
                ><Instagram aria-hidden className="w-4 h-4" /> @{info.instagramHandle.replace(/^@/, "")}</a>
              )}
              {info.websiteUrl && (
                <a
                  href={info.websiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="border border-foreground px-3 py-2 hover:bg-foreground hover:text-background transition-colors"
                >↗ Website</a>
              )}
            </div>
          )}
        </dl>
        {!!user && (
          <div className="mt-8 pt-4 border-t border-border">
            <DownloadButton
              src={info.image}
              name={`${info.photographerName || "om-selected"}-${info.caption || "foto"}`}
            />
          </div>
        )}
        {info.photographerId && (
          <div className="mt-8">
            {isVisagist ? (
              <Link
                to="/visagists/$id"
                params={{ id: info.photographerId }}
                className="eyebrow inline-block border border-foreground px-4 py-2 hover:bg-foreground hover:text-background transition-colors"
              >Zum Profil →</Link>
            ) : (
              <Link
                to="/photographers/$id"
                params={{ id: info.photographerId }}
                className="eyebrow inline-block border border-foreground px-4 py-2 hover:bg-foreground hover:text-background transition-colors"
              >Zum Profil →</Link>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function DownloadButton({ src, name }: { src: string; name: string }) {
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  async function onDownload() {
    setState("loading");
    try {
      const safe = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "bild";
      await downloadAsJpeg(src, `${safe}.jpg`);
      setState("idle");
    } catch {
      setState("error");
    }
  }
  return (
    <button
      type="button"
      onClick={onDownload}
      disabled={state === "loading"}
      className="eyebrow inline-flex items-center gap-2 border border-foreground px-4 py-2 hover:bg-foreground hover:text-background transition-colors disabled:opacity-60"
    >
      {state === "loading" ? "…" : state === "error" ? "Fehler – erneut versuchen" : "Als JPEG herunterladen ↓"}
    </button>
  );
}